import { useCallback, useRef } from 'react';
import { FieldValues, Path, useFieldArray, UseFormReturn } from 'react-hook-form';
import { useSnackbar } from 'notistack';

export interface UseExcelFormFieldOptions<
  TFormData extends FieldValues,
  TFieldName extends Path<TFormData>,
  TExcelRow,
  TFormRow,
> {
  methods: UseFormReturn<TFormData>;
  fieldName: TFieldName;
  downloadFn: (data: TExcelRow[]) => void;
  parseFn: (file: File) => Promise<{ data: TExcelRow[]; errors: string[] }>;
  formToExcel: (item: TFormRow) => TExcelRow;
  excelToForm: (row: TExcelRow) => TFormRow;
  labels: {
    download: string;
    upload: string;
    empty: string;
  };
}

export function useExcelFormField<
  TFormData extends FieldValues,
  TFieldName extends Path<TFormData>,
  TExcelRow,
  TFormRow,
>({
  methods,
  fieldName,
  downloadFn,
  parseFn,
  formToExcel,
  excelToForm,
  labels,
}: UseExcelFormFieldOptions<TFormData, TFieldName, TExcelRow, TFormRow>) {
  const { enqueueSnackbar } = useSnackbar();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { fields, append, remove, insert, replace } = useFieldArray({
    control: methods.control,
    name: fieldName as never,
  });

  const handleDownload = useCallback(() => {
    try {
      const currentData = (methods.getValues(fieldName) as TFormRow[]) || [];
      const excelData = currentData.map(formToExcel);
      downloadFn(excelData);
      enqueueSnackbar('엑셀 파일이 다운로드되었습니다.', { variant: 'success' });
    } catch (error) {
      console.error(error);
      enqueueSnackbar('다운로드 중 오류가 발생했습니다.', { variant: 'error' });
    }
  }, [methods, fieldName, downloadFn, formToExcel, enqueueSnackbar]);

  const handleUpload = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      try {
        const file = event.target.files?.[0];
        if (!file) return;

        const { data, errors } = await parseFn(file);

        if (errors.length > 0) {
          errors.forEach((err) => {
            enqueueSnackbar(err, { variant: 'warning' });
          });
        }

        if (data.length > 0) {
          const formData = data.map(excelToForm);
          replace(formData as never);
          enqueueSnackbar(`${data.length}${labels.upload}`, {
            variant: 'success',
          });
        } else if (errors.length === 0) {
          enqueueSnackbar(labels.empty, { variant: 'info' });
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      } catch (error) {
        console.error(error);
        enqueueSnackbar('업로드 중 오류가 발생했습니다.', { variant: 'error' });
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
      }
    },
    [parseFn, excelToForm, replace, enqueueSnackbar, labels],
  );

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  return {
    fields,
    append,
    remove,
    insert,
    replace,
    fileInputRef,
    handleDownload,
    handleUpload,
    handleClickUpload,
  };
}
